import { lazy } from 'react'
import { Navigate, useParams, type RouteObject } from 'react-router-dom'

// Wrapper to substitute :id param into Navigate target (Navigate.to is literal,
// not a route pattern, so "/maturity/respond/:id" would route literally to ":id").
function NavigateWithId({ to }: { to: string }) {
  const { id } = useParams<{ id: string }>()
  const target = id ? to.replace(':id', id) : to.replace('/:id', '')
  return <Navigate to={target} replace />
}

// Generic Maturity Platform pages
const MaturityDashboardPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityDashboardPage }))
)
const FrameworksCatalogPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.FrameworksCatalogPage }))
)
const ClientsOrgsPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.ClientsOrgsPage }))
)
const MaturityCreateWizardPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityCreateWizardPage }))
)
const MaturityAssessmentListPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityAssessmentListPage }))
)
const MaturityRespondPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityRespondPage }))
)
const MaturityConsolidationPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityConsolidationPage }))
)
const MaturityResultsPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityResultsPage }))
)
const MaturityGapAnalysisPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityGapAnalysisPage }))
)
const MaturityDomainAnalysisPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityDomainAnalysisPage }))
)
const MaturityDimensionAnalysisPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityDimensionAnalysisPage }))
)
const MaturityBenchmarkPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityBenchmarkPage }))
)
const MaturityTargetDefinitionPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityTargetDefinitionPage }))
)
const MaturityRoadmapPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityRoadmapPage }))
)
const MaturityEvolutionPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityEvolutionPage }))
)
const MaturityReportPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityReportPage }))
)
const MaturityDirectionReviewPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityDirectionReviewPage }))
)
const MaturityTemplatesPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturityTemplatesPage }))
)
const MaturitySettingsPage = lazy(() =>
  import('../pages/maturity').then((m) => ({ default: m.MaturitySettingsPage }))
)

// ISO 42001 legacy pages
const ISO42001TemplateEditorPage = lazy(() =>
  import('../pages/iso42001').then((m) => ({ default: m.ISO42001TemplateEditorPage }))
)

export const maturityRoutes: RouteObject[] = [
  // Generic Maturity Platform
  { path: '/maturity', element: <MaturityDashboardPage /> },
  { path: '/maturity/frameworks', element: <FrameworksCatalogPage /> },
  { path: '/maturity/clients', element: <ClientsOrgsPage /> },
  { path: '/maturity/create', element: <MaturityCreateWizardPage /> },
  { path: '/maturity/assessments', element: <MaturityAssessmentListPage /> },
  { path: '/maturity/respond/:id', element: <MaturityRespondPage /> },
  { path: '/maturity/consolidation/:id', element: <MaturityConsolidationPage /> },
  { path: '/maturity/results/:id', element: <MaturityResultsPage /> },
  { path: '/maturity/gaps/:id', element: <MaturityGapAnalysisPage /> },
  { path: '/maturity/domains/:id', element: <MaturityDomainAnalysisPage /> },
  { path: '/maturity/dimensions/:id', element: <MaturityDimensionAnalysisPage /> },
  { path: '/maturity/benchmark/:id', element: <MaturityBenchmarkPage /> },
  { path: '/maturity/targets/:id', element: <MaturityTargetDefinitionPage /> },
  { path: '/maturity/roadmap/:id', element: <MaturityRoadmapPage /> },
  { path: '/maturity/evolution/:id', element: <MaturityEvolutionPage /> },
  { path: '/maturity/report/:id', element: <MaturityReportPage /> },
  { path: '/maturity/review', element: <MaturityDirectionReviewPage /> },
  { path: '/maturity/templates', element: <MaturityTemplatesPage /> },
  { path: '/maturity/settings', element: <MaturitySettingsPage /> },

  // ISO 42001 - Legacy (backward compatibility redirects)
  { path: '/iso42001', element: <Navigate to="/maturity?framework=ISO42001" replace /> },
  { path: '/iso42001/templates', element: <Navigate to="/maturity/templates" replace /> },
  { path: '/iso42001/templates/:id', element: <ISO42001TemplateEditorPage /> },
  { path: '/iso42001/create', element: <Navigate to="/maturity/create?framework=ISO42001" replace /> },
  { path: '/iso42001/respond/:id', element: <NavigateWithId to="/maturity/respond/:id" /> },
  { path: '/iso42001/consolidation/:id', element: <NavigateWithId to="/maturity/consolidation/:id" /> },
  { path: '/iso42001/results/:id', element: <NavigateWithId to="/maturity/results/:id" /> },
  { path: '/iso42001/dimensions/:id', element: <NavigateWithId to="/maturity/dimensions/:id" /> },
  { path: '/iso42001/settings', element: <Navigate to="/maturity/settings" replace /> },
]
